import React from 'react'
import PropTypes from 'prop-types'
import theme from 'styles/theme'
import { useTheme } from 'styled-components'
import { Title26, Eyebrow, Body14 } from 'components/Typography'
import Link from 'components/Link'
import MarkdownRenderer from 'components/MarkdownRenderer'
import trackGTM from 'utils/trackGTM'
import GA4 from 'utils/GA4'
import { FadeInUpAnimationWrapper } from 'styles/animation'
import { useComponentId, useFieldId } from 'utils/optimizely'
import ProductCardInfo from './ProductCard/ProductCardInfo'
import { Expand } from '../Icons'

import {
  CardImg,
  ProductCardImg,
  CardImgCopy,
  CardInfoMeta,
  CardMeta,
  CardMetaDivider,
  CardServiceMeta,
  ProductCardInfoWrapper,
  ServiceCardImgWrapper,
  ServiceCardImg,
  Wrapper,
  WrapperLink,
  BrandCardContainer,
  BrandCardImageContainer,
  BrandCardSummary,
  BrandCardEyebrow,
  BrandCardContent,
  BrandCardImage,
  BrandCardLogo,
  CardModalButton,
  ProductCardWrapper,
} from './Card.style'

import {
  OfferCardWrapper,
  OfferCardLink,
  OfferCardImage,
  OfferCardInfoWrapper,
  OfferInfoHeader,
  OfferInfoHeadline,
  OfferInfoCopy,
} from './OfferCard.style'

import InfoCardBodyText from './InfoCard.style'

const Card = ({
  image,
  article,
  info,
  offer,
  product,
  service,
  brand,
  isSmall,
  onClick,
  hasImage,
  type,
  title,
  elementId,
  onclickProductModal,
  enableImageFadeIn,
  headline,
  rtl,
  isColoredBg,
}) => {
  const { url: articleUrl } = article || { url: null }
  const { url: serviceUrl, external } = service || { url: null }
  const url = articleUrl || serviceUrl || null
  const elId = elementId || useComponentId(`Comp_Carousel-00-card`)
  const {
    colors: { sagemediumToGreen, white },
  } = useTheme()

  const cardContent = (
    <>
      {/* Article Card */}
      {article && (
        <>
          {image && image.portrait && (
            <FadeInUpAnimationWrapper enabled={enableImageFadeIn}>
              <CardImg
                id={useFieldId(elId, `image`)}
                image={image.portrait.gatsbyImageData}
                isSmall={isSmall}
                alt={image.altText || ''}
              />
            </FadeInUpAnimationWrapper>
          )}

          <CardMeta isSmall={isSmall}>
            {article && (article.eyebrow || article.publishDate) && (
              <Eyebrow pt={[theme.space[4]]} id={useFieldId(elId, `eyebrow`)}>
                {article.eyebrow || null}
                {article.eyebrow && article.publishDate && (
                  <CardMetaDivider>⬩</CardMetaDivider>
                )}
                {article.publishDate || null}
              </Eyebrow>
            )}

            {article && article.heading && (
              <Title26 mt={[theme.space[0]]} id={useFieldId(elId, `heading`)}>
                {article.heading}
              </Title26>
            )}
          </CardMeta>
        </>
      )}

      {/* Info Card */}
      {info && (
        <CardInfoMeta filled isSmall={isSmall}>
          <Title26 mt={[theme.space[0]]} id={useFieldId(elId, `heading`)}>
            {info.heading}
          </Title26>
          <MarkdownRenderer as={InfoCardBodyText} html={info.bodyCopy} />
        </CardInfoMeta>
      )}

      {/* Offer Card */}
      {offer && (
        <OfferCardWrapper
          isSmall={isSmall}
          isOfferGrid={offer?.isOfferGrid}
          hasAudience={!!offer.audiences}
          className={offer.audiences}>
          <OfferCardLink
            id={useFieldId(elId, `cta`)}
            onClick={() => {
              onClick()
              trackGTM(
                'Offer cards',
                'In page CTA',
                'Offer cards',
                offer.headline
              )
              GA4('offer_cards', {
                brand_name: offer.brandName || 'non brand specific',
              })
              GA4('offer_cards_click', {
                component_headline: headline || offer.componentHeadline,
                headline: offer.headline,
                brand_name: offer.brandName || 'non brand specific',
                village_name: offer.villageSlug,
              })
            }}
            as={Link}
            to={offer.url}>
            {image && (image.portrait || image.landscape) && (
              <FadeInUpAnimationWrapper enabled={enableImageFadeIn}>
                <OfferCardImage
                  id={useFieldId(elId, `image`)}
                  image={
                    image?.portrait?.gatsbyImageData ||
                    image?.landscape?.gatsbyImageData
                  }
                  alt={image.altText || ''}
                />
              </FadeInUpAnimationWrapper>
            )}

            <OfferCardInfoWrapper>
              <OfferInfoHeader>
                <Eyebrow id={useFieldId(elId, `eyebrow`)}>
                  {offer.eyebrow}
                </Eyebrow>
                <OfferInfoHeadline id={useFieldId(elId, `headline`)}>
                  {offer.headline}
                </OfferInfoHeadline>
              </OfferInfoHeader>

              <Eyebrow
                mb={[2]}
                id={useFieldId(elId, `eyebrow`)}
                dir={rtl ? 'rtl' : null}>
                {`${offer.offerRunStartDate} - ${offer.offerRunFinishDate}`}
              </Eyebrow>

              <MarkdownRenderer as={OfferInfoCopy} html={offer.description} />
            </OfferCardInfoWrapper>
          </OfferCardLink>
        </OfferCardWrapper>
      )}

      {/* Product Card */}
      {product && (
        <FadeInUpAnimationWrapper enabled={enableImageFadeIn}>
          <ProductCardWrapper onClick={onclickProductModal}>
            <CardModalButton>
              <Expand width={30} fill={sagemediumToGreen} />
            </CardModalButton>
            {image && image.portrait && (
              <ProductCardImg
                image={image.portrait.gatsbyImageData}
                isSmall={isSmall}
                alt={image.altText || ''}
                id={useFieldId(elId, `image`)}
              />
            )}

            <CardMeta isSmall={isSmall}>
              {product && (
                <ProductCardInfoWrapper id={useFieldId(elId, `product`)}>
                  <ProductCardInfo
                    {...{
                      brand: product.brand,
                      currency: product.currency,
                      description: product.description,
                      locale: product.locale,
                      rrpPrice: product.rrpPrice,
                      salePrice: product.salePrice,
                      villagePrice: product.villagePrice,
                      labels: product.villageLevelLabels,
                      isColoredBg,
                    }}
                  />
                </ProductCardInfoWrapper>
              )}
            </CardMeta>
          </ProductCardWrapper>
        </FadeInUpAnimationWrapper>
      )}

      {/* Service Card */}
      {service && (
        <>
          <ServiceCardImgWrapper>
            {image && image.portrait && (
              <FadeInUpAnimationWrapper enabled={enableImageFadeIn}>
                <ServiceCardImg
                  id={useFieldId(elId, `image`)}
                  image={image.portrait.gatsbyImageData}
                  isSmall={isSmall}
                  alt={image.altText || ''}
                />
              </FadeInUpAnimationWrapper>
            )}

            <CardImgCopy>
              <Eyebrow
                color={white}
                pt={[theme.space[4]]}
                id={useFieldId(elId, `eyebrow`)}>
                {service.eyebrow}
              </Eyebrow>
              <Title26 mt={[theme.space[0]]} id={useFieldId(elId, `heading`)}>
                {service.heading}
              </Title26>
            </CardImgCopy>
          </ServiceCardImgWrapper>

          <CardServiceMeta>
            {service && service.benefit && (
              <Eyebrow
                pt={[theme.space[4]]}
                id={useFieldId(elId, `eyebrow-benefit`)}>
                <strong>{service.benefit}</strong>
              </Eyebrow>
            )}

            {service && service.bodyCopy && (
              <Body14 mt={[theme.space[0]]} id={useFieldId(elId, `copy`)}>
                {service.bodyCopy}
              </Body14>
            )}
          </CardServiceMeta>
        </>
      )}

      {/* Service Card */}
      {brand && (
        <BrandCardContainer hasImage={hasImage}>
          <BrandCardImageContainer>
            {image.portrait && (
              <BrandCardImage
                id={useFieldId(elId, `image`)}
                image={image.portrait.gatsbyImageData}
                alt={image.altText || ''}
              />
            )}
            {brand.logo && brand.logo.code && (
              <FadeInUpAnimationWrapper enabled={enableImageFadeIn}>
                <BrandCardLogo
                  id={useFieldId(elId, `logo`)}
                  dangerouslySetInnerHTML={{ __html: brand.logo.code.code }}
                  hasImage={hasImage}
                />
              </FadeInUpAnimationWrapper>
            )}
          </BrandCardImageContainer>
          <BrandCardContent>
            {brand.eyebrow && (
              <BrandCardEyebrow id={useFieldId(elId, `eyebrow`)}>
                {brand.eyebrow}
              </BrandCardEyebrow>
            )}
            <BrandCardSummary id={useFieldId(elId, `summary`)}>
              {brand.summary}
            </BrandCardSummary>
          </BrandCardContent>
        </BrandCardContainer>
      )}
    </>
  )

  return url ? (
    <WrapperLink
      onClick={(e) => {
        onClick(e)
        trackGTM('carousel', 'carousel', type, title)

        if (product) {
          trackGTM(
            'Virtual Shopping',
            'VS product card',
            title,
            product.description
          )
        }
      }}
      to={url}
      external={external}
      isOffer={offer}>
      {cardContent}
    </WrapperLink>
  ) : (
    <Wrapper
      onClick={(e) => {
        e.preventDefault()
        trackGTM('carousel', 'carousel', type, title)

        if (product) {
          trackGTM(
            'Virtual Shopping',
            'VS product card',
            title,
            product.description
          )
        }
      }}
      isOffer={offer}>
      {cardContent}
    </Wrapper>
  )
}

Card.propTypes = {
  elementId: PropTypes.string,
  image: PropTypes.shape({
    headline: PropTypes.string,
    altText: PropTypes.string,
    landscape: PropTypes.shape({
      gatsbyImageData: PropTypes.shape({
        aspectRatio: PropTypes.number.isRequired,
        src: PropTypes.string.isRequired,
        srcSet: PropTypes.string.isRequired,
        srcSetWebp: PropTypes.string.isRequired,
        sizes: PropTypes.string.isRequired,
      }),
    }),
    portrait: PropTypes.shape({
      gatsbyImageData: PropTypes.shape({
        aspectRatio: PropTypes.number.isRequired,
        src: PropTypes.string.isRequired,
        srcSet: PropTypes.string.isRequired,
        srcSetWebp: PropTypes.string.isRequired,
        sizes: PropTypes.string.isRequired,
      }),
    }),
  }),
  article: PropTypes.shape({
    eyebrow: PropTypes.string,
    url: PropTypes.string.isRequired,
    publishDate: PropTypes.string,
    heading: PropTypes.string,
  }),
  info: PropTypes.shape({
    heading: PropTypes.string,
    bodyCopy: PropTypes.string,
  }),
  offer: PropTypes.shape({
    eyebrow: PropTypes.string.isRequired,
    headline: PropTypes.string.isRequired,
    offerRunStartDate: PropTypes.string.isRequired,
    offerRunFinishDate: PropTypes.string.isRequired,
    offerExpirationLabel: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
    audiences: PropTypes.string,
    isOfferGrid: PropTypes.bool,
    brandName: PropTypes.string,
    villageSlug: PropTypes.string,
    componentHeadline: PropTypes.string,
  }),
  product: PropTypes.shape({
    brand: PropTypes.string,
    currency: PropTypes.string,
    description: PropTypes.string,
    locale: PropTypes.string,
    rrpPrice: PropTypes.number,
    salePrice: PropTypes.number,
    villagePrice: PropTypes.number,
    villageLevelLabels: PropTypes.shape({}),
  }),
  service: PropTypes.shape({
    eyebrow: PropTypes.string,
    benefit: PropTypes.string,
    heading: PropTypes.string,
    bodyCopy: PropTypes.string,
    url: PropTypes.string,
  }),
  brand: PropTypes.shape({
    eyebrow: PropTypes.string,
    summary: PropTypes.string,
    logo: PropTypes.shape({
      code: PropTypes.shape({ code: PropTypes.string }),
    }),
  }),
  isSmall: PropTypes.bool,
  onClick: PropTypes.func,
  hasImage: PropTypes.bool,
  type: PropTypes.string,
  title: PropTypes.string,
  onclickProductModal: PropTypes.func,
  enableImageFadeIn: PropTypes.bool,
  rtl: PropTypes.bool,
  headline: PropTypes.string,
  isColoredBg: PropTypes.bool,
}

Card.defaultProps = {
  elementId: null,
  article: null,
  info: null,
  offer: null,
  product: null,
  service: null,
  brand: null,
  isSmall: false,
  onClick: () => {},
  hasImage: false,
  image: null,
  type: null,
  title: null,
  onclickProductModal: () => {},
  enableImageFadeIn: false,
  rtl: null,
  headline: '',
  isColoredBg: false,
}

export default Card
